/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilterOutlined,
  FolderOpenOutlined,
  LockOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { colors } from "../../Utils/temas";
import { useUserContext } from "../../contexts/usersContext";
import { implantacaoRequest } from "./request";
import "./estilos.css";

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const statusImplantacaoOptions = [
  { label: "Planejada", value: "Planejada" },
  { label: "Em andamento", value: "Em andamento" },
  { label: "Concluida", value: "Concluida" },
  { label: "Cancelada", value: "Cancelada" },
];

const statusFaseOptions = [
  { label: "Nao iniciada", value: "Nao iniciada" },
  { label: "Em andamento", value: "Em andamento" },
  { label: "Concluida", value: "Concluida" },
];

const statusChecklistOptions = [
  { label: "Pendente", value: "Pendente" },
  { label: "Concluido", value: "Concluido" },
  { label: "N/A", value: "N/A" },
];

const statusValidacaoOptions = [
  { label: "Aprovado", value: "Aprovado" },
  { label: "Reprovado", value: "Reprovado" },
  { label: "Pendente ajustes", value: "Pendente ajustes" },
];

const tipoGrupoOptions = [
  { label: "Grupo A", value: "A" },
  { label: "Grupo B", value: "B" },
];

const statusFinanceiroOptions = [
  { label: "Aprovado", value: "Aprovado" },
  { label: "Reprovado", value: "Reprovado" },
  { label: "Pendente", value: "Pendente" },
];

const marcoFinanceiroOptions = [
  { label: "Sim", value: "S" },
  { label: "Nao", value: "N" },
];

const tipoEvidenciaOptions = [
  { label: "Documento", value: "Documento" },
  { label: "Video", value: "Video" },
  { label: "Imagem", value: "Imagem" },
  { label: "Relatorio", value: "Relatorio" },
  { label: "Outro", value: "Outro" },
];

const ufOptions = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
].map((uf) => ({ label: uf, value: uf }));

const situacaoServentiaOptions = [
  { label: "Provido", value: "P" },
  { label: "Vago", value: "V" },
];

const versaoBancoOptions = [
  { label: "Firebird 2.5", value: "FB25" },
  { label: "Firebird 3.0", value: "FB30" },
  { label: "Firebird 5.0", value: "FB50" },
];

const statusColors = {
  Planejada: "gold",
  "Em andamento": "processing",
  Concluida: "success",
  Cancelada: "error",
  "Nao iniciada": "default",
  Pendente: "warning",
  Concluido: "success",
  "N/A": "default",
  Aprovado: "success",
  Reprovado: "error",
  "Pendente ajustes": "warning",
};

// Formata datas para exibição no padrão brasileiro.
const formatDate = (value, withTime = true) => {
  if (!value) return "-";
  return withTime
    ? moment(value).format("DD/MM/YYYY HH:mm")
    : moment(value).format("DD/MM/YYYY");
};

// Converte datas do formulário para ISO antes de enviar para a API.
const formatDateForRequest = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) {
    return value.isValid() ? value.toISOString() : null;
  }

  if (typeof value === "string") {
    const parsed = moment(value, ["DD/MM/YYYY HH:mm", "DD/MM/YYYY"], true);
    if (parsed.isValid()) return parsed.toISOString();
  }

  const fallback = moment(value);
  return fallback.isValid() ? fallback.toISOString() : null;
};
const isChecklistDoneStatus = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  return normalizedStatus.startsWith("conclu") || normalizedStatus === "n/a";
};

// Calcula o percentual concluído do checklist da fase.
const getChecklistProgress = (mergedChecklist) => {
  if (!mergedChecklist.length) return 0;
  const done = mergedChecklist.filter((item) =>
    isChecklistDoneStatus(item.status),
  ).length;
  return Math.round((done / mergedChecklist.length) * 100);
};

// Renderiza o status em formato visual (Tag do Ant Design).
const getStatusTag = (status) => (
  <Tag color={statusColors[status] || "default"}>{status || "Sem status"}</Tag>
);

const getGrupoTag = (tipoGrupo) => {
  if (!tipoGrupo) return null;
  const upper = String(tipoGrupo).toUpperCase();
  if (upper === "A") {
    return <Tag color="blue">[A]</Tag>;
  }
  if (upper === "B") {
    return <Tag color="orange">[B]</Tag>;
  }
  return <Tag>[{upper}]</Tag>;
};

// Extrai a melhor mensagem de erro possível da resposta da API.
const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;

  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];

  if (Array.isArray(firstValue) && firstValue.length) {
    return firstValue[0];
  }

  if (typeof firstValue === "string") {
    return firstValue;
  }

  return fallback;
};

// Componente base de modal com formulário reutilizável.
const getNotApplicableChecklistIdsFromError = (error) => {
  const parseIdsFromText = (raw) => {
    if (!raw || typeof raw !== "string") return [];
    const match = raw.match(/\[(.*?)\]/);
    if (!match?.[1]) return [];

    return match[1]
      .split(",")
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isFinite(value));
  };

  const data = error?.response?.data;

  const message = getApiErrorMessage(error, "");
  const idsFromMessage = parseIdsFromText(message);
  if (idsFromMessage.length) return idsFromMessage;

  if (typeof data === "string") {
    const ids = parseIdsFromText(data);
    if (ids.length) return ids;
  }

  if (Array.isArray(data)) {
    const ids = parseIdsFromText(data.join(" "));
    if (ids.length) return ids;
  }

  if (data && typeof data === "object") {
    const rawValues = Object.values(data);
    const values = rawValues.flatMap((value) =>
      Array.isArray(value) ? value : [value],
    );

    const idsFromObjectText = parseIdsFromText(
      values.filter((value) => typeof value === "string").join(" "),
    );
    if (idsFromObjectText.length) return idsFromObjectText;

    const numericArrays = rawValues.filter(
      (value) =>
        Array.isArray(value) &&
        value.every((item) => Number.isFinite(Number(item))),
    );
    if (numericArrays.length) {
      return [...new Set(numericArrays.flat().map((item) => Number(item)))];
    }

    const idsFromJson = parseIdsFromText(JSON.stringify(data));
    if (idsFromJson.length) return idsFromJson;
  }

  return [];
};

const EntityModal = ({
  title,
  visible,
  onCancel,
  onSubmit,
  loading,
  form,
  children,
}) => (
  <Modal
    title={title}
    visible={visible}
    onCancel={onCancel}
    onOk={() => form.submit()}
    okText="Salvar"
    cancelText="Cancelar"
    confirmLoading={loading}
    destroyOnClose
    width={720}
  >
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      {children}
    </Form>
  </Modal>
);

// Modal de cadastro/edição da implantação com todos os campos principais.
const ImplantacaoFormModal = ({
  visible,
  onCancel,
  onSubmit,
  loading,
  form,
  sistemas,
  cartorios,
  usuarios,
}) => {
  const selectedCartorioId = Form.useWatch("cartorio", form);

  useEffect(() => {
    if (!selectedCartorioId) return;
    const cartorio = cartorios.find((item) => item.id === selectedCartorioId);
    if (!cartorio) return;

    form.setFieldsValue({
      nome_cartorio: cartorio.nome_cartorio,
      cidade: cartorio.cidade,
      uf: cartorio.uf,
      cpfcnpj: cartorio.cpfcnpj,
      cns: cartorio.cns,
      situacao_serventia: cartorio.situacao_serventia,
      versao_banco: cartorio.versao_banco,
    });
  }, [selectedCartorioId]);

  return (
    <EntityModal
      title="Cadastro de implantação"
      visible={visible}
      onCancel={onCancel}
      onSubmit={onSubmit}
      loading={loading}
      form={form}
    >
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="sistemas_ids"
            label="Sistemas"
            rules={[
              { required: true, message: "Selecione ao menos um sistema" },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={sistemas.map((item) => ({
                label:
                  item.nome_sistema ||
                  item.nome ||
                  item.descricao ||
                  `Sistema ${item.id}`,
                value: String(item.id),
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="cartorio" label="Cartório">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={cartorios.map((item) => ({
                label: `${item.nome_cartorio} ${item.uf ? `- ${item.uf}` : ""}`,
                value: item.id,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="nome_cartorio" label="Nome do cartório">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="cidade" label="Cidade">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="uf" label="UF">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={ufOptions}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cpfcnpj" label="CPF/CNPJ">
            <Input maxLength={14} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="cns" label="CNS">
            <Input maxLength={6} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name="responsavel_interno"
            label="Responsável interno"
            rules={[
              { required: true, message: "Selecione o Responsável interno" },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={usuarios.map((item) => ({
                label: item.nome || item.email,
                value: item.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="responsavel_cliente" label="Responsável cliente">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={usuarios.map((item) => ({
                label: item.nome || item.email,
                value: item.id,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={6}>
          <Form.Item name="tipo_grupo" label="Tipo grupo">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={tipoGrupoOptions}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status_financeiro" label="Status financeiro">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={statusFinanceiroOptions}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label="Status">
            <Select
              showSearch
              optionFilterProp="label"
              options={statusImplantacaoOptions}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="situacao_serventia" label="Situação serventia">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={situacaoServentiaOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="versao_banco" label="Versão banco">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={versaoBancoOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="data_inicio" label="Data de início">
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="data_previsao_termino" label="Previsão de término">
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="data_termino_efetiva" label="Término efetivo">
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="observacoes" label="Observações">
        <TextArea rows={4} />
      </Form.Item>
    </EntityModal>
  );
};

// Página principal do módulo de implantação (dashboard, fluxos, cadastros e relatórios).
export const ImplantacaoPage = () => {
  const { currentUser } = useUserContext();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawerFilterVisible, setDrawerFilterVisible] = useState(false);
  const [implantacaoModalVisible, setImplantacaoModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [phaseVisible, setPhaseVisible] = useState(false);
  const [basicModalVisible, setBasicModalVisible] = useState(false);
  const [responseVisible, setResponseVisible] = useState(false);
  const [evidenceVisible, setEvidenceVisible] = useState(false);
  const [validationVisible, setValidationVisible] = useState(false);
  const [reportVisible, setReportVisible] = useState(false);

  const [activeCadastroTab, setActiveCadastroTab] = useState("categorias");
  const [implantacoes, setImplantacoes] = useState({ count: 0, results: [] });
  const [categorias, setCategorias] = useState([]);
  const [fases, setFases] = useState([]);
  const [itensChecklist, setItensChecklist] = useState([]);
  const [sistemas, setSistemas] = useState([]);
  const [cartorios, setCartorios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [relatorio, setRelatorio] = useState(null);
  const [filters, setFilters] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedImplantacaoId, setSelectedImplantacaoId] = useState(null);
  const [selectedImplantacao, setSelectedImplantacao] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseDetails, setPhaseDetails] = useState({});
  const [phaseItemsToAdd, setPhaseItemsToAdd] = useState([]);
  const [fasesDisponiveis, setFasesDisponiveis] = useState([]);
  const [faseParaAdicionar, setFaseParaAdicionar] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responseEvidences, setResponseEvidences] = useState([]);
  const [editingEntity, setEditingEntity] = useState(null);
  const [entityType, setEntityType] = useState("categorias");

  const [implantacaoPage, setImplantacaoPage] = useState(1);
  const [implantacaoPageSize, setImplantacaoPageSize] = useState(10);

  const [formFilter] = Form.useForm();
  const [formImplantacao] = Form.useForm();
  const [formBasic] = Form.useForm();
  const [formPhase] = Form.useForm();
  const [formResponse] = Form.useForm();
  const [formEvidence] = Form.useForm();
  const [formValidation] = Form.useForm();
  const [uploadFileList, setUploadFileList] = useState([]);
  const reportContentRef = useRef(null);
  const detailRequestRef = useRef(0);
  const checklistTopScrollRef = useRef(null);
  const checklistTopScrollInnerRef = useRef(null);
  const checklistTableWrapRef = useRef(null);
  const checklistScrollSyncRef = useRef(false);
  const selectedBasicFaseId = Form.useWatch("fase", formBasic);

  const selectedPhaseBundle = selectedPhase
    ? phaseDetails[selectedPhase.id]
    : null;
  const screens = useBreakpoint();
  const isSmallPhaseDrawer = !screens.lg;

  const getChecklistScrollableElement = () =>
    checklistTableWrapRef.current?.querySelector(
      ".ant-table-content, .ant-table-body",
    );

  const syncChecklistTopScrollbar = () => {
    const topScrollbar = checklistTopScrollRef.current;
    const topScrollbarInner = checklistTopScrollInnerRef.current;
    const tableScrollable = getChecklistScrollableElement();

    if (!topScrollbar || !topScrollbarInner || !tableScrollable) {
      return;
    }

    topScrollbarInner.style.width = `${tableScrollable.scrollWidth}px`;
    topScrollbar.style.display =
      tableScrollable.scrollWidth > tableScrollable.clientWidth ? "block" : "none";
    topScrollbar.scrollLeft = tableScrollable.scrollLeft;
  };


  // Normaliza IDs vindos como número, string ou objeto.
  const getIdFromValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "object") {
      return value.id ?? value.value ?? null;
    }
    return value;
  };

  // Compara dois IDs com segurança independente do tipo.
  const isSameId = (left, right) => {
    if (
      left === null ||
      left === undefined ||
      right === null ||
      right === undefined
    ) {
      return false;
    }
    return String(left) === String(right);
  };

  // Resolve o nome/descrição da fase com fallback entre fontes diferentes.
  const getFaseDescricao = (phase) => {
    const faseId =
      getIdFromValue(phase?.fase) ||
      phase?.fase_id ||
      phase?.faseId ||
      phase?.fase_codigo;
    const faseCadastro = fases.find((item) =>
      isSameId(item.id || item.fase_id, faseId),
    );

    return (
      phase?.fase_descricao ||
      phase?.fase_nome ||
      phase?.nome_fase ||
      phase?.nome ||
      phase?.fase?.nome ||
      phase?.fase?.descricao ||
      faseCadastro?.nome ||
      faseCadastro?.descricao ||
      (faseId ? `Fase ${faseId}` : "Sem fase")
    );
  };

  // Resolve a descrição da categoria para itens e respostas do checklist.
  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const getFaseIdFromOption = (phase) =>
    getIdFromValue(phase?.fase_id) ||
    getIdFromValue(phase?.fase) ||
    phase?.id ||
    phase?.value;

  const getFaseLabelFromOption = (phase) =>
    phase?.nome ||
    phase?.descricao ||
    phase?.fase_descricao ||
    phase?.label ||
    "";

  const getFaseSetKey = (phase) => {
    const rawValue =
      phase?.set_id ??
      phase?.set ??
      phase?.set_unico ??
      phase?.set_fase ??
      phase?.fase_set_id ??
      phase?.fase_set ??
      phase?.conjunto ??
      phase?.grupo ??
      phase?.grupo_fase ??
      phase?.fase?.set_id ??
      phase?.fase?.set ??
      phase?.fase?.set_unico ??
      null;
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return null;
    }
    return String(rawValue);
  };

  const isPreImplantacaoLabel = (label) => {
    const normalized = normalizeText(label);
    return normalized.includes("pre") && normalized.includes("implant");
  };

  const isImplantacaoLabel = (label) => {
    const normalized = normalizeText(label);
    return (
      normalized.includes("implant") &&
      !normalized.includes("pre") &&
      !normalized.includes("pos")
    );
  };

  const findLinkedImplantacaoPhase = (faseId) => {
    const selectedOption = (fasesDisponiveis || []).find((item) =>
      isSameId(getFaseIdFromOption(item), faseId),
    );
    if (!selectedOption) return null;

    const selectedLabel = getFaseLabelFromOption(selectedOption);
    if (!isPreImplantacaoLabel(selectedLabel)) return null;

    const selectedSetKey = getFaseSetKey(selectedOption);
    const candidates = (fasesDisponiveis || []).filter(
      (item) => !isSameId(getFaseIdFromOption(item), faseId),
    );

    if (selectedSetKey) {
      const sameSetMatch = candidates.find((item) => {
        if (getFaseSetKey(item) !== selectedSetKey) return false;
        return isImplantacaoLabel(getFaseLabelFromOption(item));
      });
      if (sameSetMatch) return sameSetMatch;
    }

    return candidates.find((item) =>
      isImplantacaoLabel(getFaseLabelFromOption(item)),
    );
  };

  const getCategoriaDescricao = (record) => {
    const categoriaId =
      getIdFromValue(record?.categoria) ||
      record?.categoria_id ||
      getIdFromValue(record?.item?.categoria) ||
      record?.item?.categoria_id;
    const categoria = categorias.find((item) =>
      isSameId(item.id || item.categoria_id, categoriaId),
    );

    return (
      record?.categoria_descricao ||
      record?.categoria_nome ||
      record?.item?.categoria_descricao ||
      record?.item?.categoria_nome ||
      categoria?.nome ||
      categoria?.descricao ||
      "Sem categoria"
    );
  };

  // Normaliza o campo obrigatório para booleano.
  const getObrigatorioValue = (record) => {
    const rawValue =
      record?.obrigatorio ??
      record?.item?.obrigatorio ??
      record?.eh_obrigatorio ??
      record?.item?.eh_obrigatorio ??
      false;

    if (typeof rawValue === "string") {
      return ["true", "1", "sim", "s"].includes(rawValue.toLowerCase());
    }

    return !!rawValue;
  };

  // Extrai IDs de sistemas a partir de diferentes formatos retornados pela API.
  const getSistemaIdsFromRecord = (record = {}) => {
    const fromList = Array.isArray(record.sistemas_ids)
      ? record.sistemas_ids
      : [];
    const fromLinked = Array.isArray(record.sistemas_vinculados)
      ? record.sistemas_vinculados.map((item) => item?.sistema)
      : [];

    const fromSistemas = Array.isArray(record.sistemas)
      ? record.sistemas.map((item) => item?.sistema)
      : [];
    const fromSingle = [
      record.sistema,
      record.sistema_id,
      record.sistemaId,
    ].filter(Boolean);

    const values = [
      ...fromList,
      ...fromLinked,
      ...fromSistemas,
      ...fromSingle,
    ].filter((item) => item !== null && item !== undefined && item !== "");
    return [...new Set(values.map((item) => String(item)))];
  };

  // Monta texto amigável com os nomes dos sistemas vinculados.
  const getSistemaNomes = (base = {}) => {
    const nomesLinked = (base.sistemas_vinculados || [])
      .map((item) => item?.sistema_descricao || item?.nome || item?.descricao)
      .filter(Boolean);
    if (nomesLinked.length) return nomesLinked.join(", ");

    const nomesSistemas = (base.sistemas || [])
      .map((item) => item?.sistema_descricao)
      .filter(Boolean);
    if (nomesSistemas.length) return nomesSistemas.join(", ");

    return base.sistema_descricao || "-";
  };

  // Extrai ID do item da resposta quando item vem como objeto ou valor simples.
  const getChecklistResponseItemId = (response) =>
    (typeof response?.item === "object"
      ? response?.item?.id
      : response?.item) ||
    response?.item_checklist ||
    response?.item_checklist_id ||
    response?.item_id ||
    response?.checklist_item ||
    response?.checklist_item_id;

  // Extrai ID da resposta de checklist (quando existir).
  const getChecklistResponseId = (response) =>
    response?.responseId ||
    response?.resposta_id ||
    response?.resposta_checklist_id ||
    response?.resposta ||
    response?.id_resposta ||
    null;

  const getChecklistDependencyBlockReason = (row, checklistRows = []) => {
    if (!row) return "Item indisponivel.";

    if (row?.pode_responder === false) {
      return row?.status_exibicao || row?.motivo_bloqueio || "Item bloqueado.";
    }

    const dependencyId =
      getIdFromValue(row?.item_dependencia) || row?.item_dependencia_id || null;

    if (dependencyId) {
      const dependencyRow = checklistRows.find((item) =>
        isSameId(item?.id || item?.itemId, dependencyId),
      );

      if (
        dependencyRow?.marco_financeiro === "S" &&
        !isChecklistDoneStatus(dependencyRow?.status)
      ) {
        return `Dependencia financeira pendente: ${dependencyRow?.descricao || "item anterior"}.`;
      }
    }

    if (row?.marco_financeiro !== "S") {
      const previousFinancialPending = checklistRows
        .filter(
          (item) =>
            item?.marco_financeiro === "S" &&
            (item?.ordem || 0) < (row?.ordem || 0),
        )
        .find((item) => !isChecklistDoneStatus(item?.status));

      if (previousFinancialPending) {
        return `Marco financeiro pendente: ${previousFinancialPending?.descricao || "item anterior"}.`;
      }
    }

    return row?.status_exibicao || row?.motivo_bloqueio || null;
  };

  const getChecklistItemCanRespond = (row, checklistRows = []) =>
    !getChecklistDependencyBlockReason(row, checklistRows);

  const getChecklistItemBlockedReason = (row, checklistRows = []) =>
    getChecklistDependencyBlockReason(row, checklistRows);

  // Define se o item retornado pela API está realmente selecionado na fase.
  const isChecklistRowSelected = (row) => {
    if (!row) return false;
    if (row.selecionado === true || row.selected === true) return true;
    if (getChecklistResponseId(row)) return true;
    if (row.implantacao_fase || row.controle_fase) return true;
    return false;
  };

  // Busca os itens de checklist cadastrados para a fase.
  const getChecklistItemsByPhase = (phase) => {
    const phaseId =
      getIdFromValue(phase?.fase) || phase?.fase_id || phase?.faseId || null;
    return itensChecklist
      .filter((item) => {
        const itemPhaseId =
          getIdFromValue(item?.fase) || item?.fase_id || item?.faseId;
        return isSameId(itemPhaseId, phaseId);
      })
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  };

  // Carrega dados auxiliares (sistemas, cartórios, usuários, fases, categorias e itens).
  const loadSupportData = async () => {
    try {
      const [
        sistemasResponse,
        cartoriosResponse,
        usuariosResponse,
        categoriasResponse,
        fasesResponse,
        itensResponse,
      ] = await Promise.all([
        implantacaoRequest.getSistemas({ limit: 200 }),
        implantacaoRequest.getCartorios({ limit: 300, situacao: "A" }),
        implantacaoRequest.getUsuarios({ limit: 200 }),
        implantacaoRequest.getCategorias(1, 200, { ordering: "ordem,nome" }),
        implantacaoRequest.getFases(1, 100, { ordering: "ordem,nome" }),
        implantacaoRequest.getItensChecklist(1, 500, {
          ordering: "fase,ordem,descricao",
        }),
      ]);

      setSistemas(sistemasResponse.data.results || []);
      setCartorios(cartoriosResponse.data.results || []);
      setUsuarios(usuariosResponse.data.results || []);
      setCategorias(categoriasResponse.data.results || []);
      setFases(fasesResponse.data.results || []);
      setItensChecklist(itensResponse.data.results || []);
    } catch (error) {
      console.error(error);
      messageApi.error("Nao foi possivel carregar os cadastros de apoio.");
    }
  };

  // Carrega a lista paginada de implantações com filtros.
  const loadImplantacoes = async (
    page = implantacaoPage,
    pageSize = implantacaoPageSize,
    params = filters,
    options = {},
  ) => {
    const { silent = false, retry = false } = options;
    try {
      setLoading(true);
      const response = await implantacaoRequest.getImplantacoes(
        page,
        pageSize,
        params,
      );
      setImplantacoes(response.data);
      setImplantacaoPage(page);
      setImplantacaoPageSize(pageSize);
      return response.data;
    } catch (error) {
      console.error(error);
      if (retry) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        return loadImplantacoes(page, pageSize, params, {
          silent,
          retry: false,
        });
      }

      if (!silent) {
        messageApi.error("Nao foi possivel carregar as implantacoes.");
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  // Atualiza/inclui a implantação salva na lista local sem recarregar toda a página.
  const upsertImplantacaoInList = (record, isNew) => {
    setImplantacoes((prevState) => {
      const results = (prevState.results || []).filter(
        (item) => item.id !== record.id,
      );

      return {
        ...prevState,
        count: isNew ? prevState.count + 1 : prevState.count,
        results: [record, ...results],
      };
    });
  };

  // Normaliza o payload do fluxo da implantação para um formato único no front.
  const normalizeFluxoResponse = (fluxoData, fallbackDetail) => {
    const detail = fluxoData?.implantacao || fallbackDetail || {};
    const fasesFluxo =
      fluxoData?.fases || detail?.fases || fluxoData?.controle_fases || [];
    const disponiveis =
      fluxoData?.fases_disponiveis || fluxoData?.fasesDisponiveis || [];

    const bundles = {};
    (fasesFluxo || []).forEach((phase) => {
      bundles[phase.id] = {
        responses:
          phase.respostas_checklist ||
          phase.respostas ||
          phase.checklist_respostas ||
          [],
        validations: phase.validacoes || [],
      };
    });

    return {
      detail: {
        ...detail,
        sistemas_vinculados:
          fluxoData?.sistemas ||
          fluxoData?.sistemas_vinculados ||
          detail?.sistemas_vinculados ||
          [],
        pode_iniciar_implantacao_real:
          fluxoData?.pode_iniciar_implantacao_real ??
          detail?.pode_iniciar_implantacao_real ??
          null,
        motivo_bloqueio_implantacao_real:
          fluxoData?.motivo_bloqueio_implantacao_real ??
          detail?.motivo_bloqueio_implantacao_real ??
          null,
        fases: fasesFluxo,
      },
      bundles,
      disponiveis,
    };
  };

  // Exporta o relatório consolidado em PDF via janela de impressão do navegador.
  const exportReportToPdf = () => {
    const content = reportContentRef.current;
    if (!content) {
      messageApi.error("Nao foi possivel gerar o PDF.");
      return;
    }

    const popup = window.open("", "_blank", "width=1200,height=900");
    if (!popup) {
      messageApi.error("Habilite pop-ups para exportar o PDF.");
      return;
    }

    popup.document.write(`
      <html>
        <head>
          <title>Relatorio de implantacao</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #1f2937; }
            h1, h2, h3, h4, h5 { margin: 0 0 8px; }
            .ant-card { border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 14px; padding: 12px; }
            .ant-divider { border-top: 1px solid #e5e7eb; margin: 12px 0; }
            .ant-list-item { padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
            .ant-list-item:last-child { border-bottom: none; }
            .ant-tag { display: inline-block; border: 1px solid #d1d5db; border-radius: 8px; padding: 2px 8px; margin-right: 6px; }
            .ant-descriptions { width: 100%; border-collapse: collapse; }
            .ant-descriptions-item-label { font-weight: 600; min-width: 180px; }
            .ant-descriptions-item-content, .ant-descriptions-item-label { border: 1px solid #e5e7eb; padding: 8px; }
            .signature-block { margin-top: 36px; }
            .signature-line { margin-top: 48px; border-top: 1px solid #111827; width: 360px; padding-top: 8px; }
            .signature-meta { margin-top: 8px; color: #4b5563; font-size: 12px; }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `);
    popup.document.close();
    popup.focus();

    setTimeout(() => {
      popup.print();
      popup.close();
    }, 250);
  };

  const openDetailDrawer = (record) => {
    const implantacaoId = getIdFromValue(record?.id ?? record);
    if (!implantacaoId) return;

    const baseRecord =
      typeof record === "object" && record !== null ? record : null;

    setSelectedImplantacaoId(implantacaoId);
    setSelectedImplantacao(baseRecord);
    setSelectedPhase(null);
    setPhaseDetails({});
    setFasesDisponiveis([]);
    setFaseParaAdicionar(null);
    setHistorico([]);
    setRelatorio(null);
    setDetailLoading(true);
    setDetailVisible(true);
  };

  // Carrega o detalhe completo da implanta??o (fluxo, hist?rico e relat?rio).
  const loadImplantacaoDetail = async (implantacaoId) => {
    if (!implantacaoId) return null;

    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;

    try {
      setDetailLoading(true);
      const [implantacaoRes, fluxoRes, historicoRes, relatorioRes] =
        await Promise.all([
          implantacaoRequest.getImplantacao(implantacaoId),
          implantacaoRequest.getFluxoImplantacao(implantacaoId),
          implantacaoRequest.getHistorico(1, 100, {
            ordering: "-data_alteracao",
          }),
          implantacaoRequest.getRelatorioImplantacao(implantacaoId),
        ]);

      const normalized = normalizeFluxoResponse(
        fluxoRes?.data,
        implantacaoRes.data,
      );

      if (detailRequestRef.current !== requestId) {
        return null;
      }

      setSelectedImplantacao(normalized.detail);
      setSelectedImplantacaoId(normalized.detail?.id || implantacaoId);
      setPhaseDetails(normalized.bundles);
      setFasesDisponiveis(normalized.disponiveis);
      setFaseParaAdicionar(null);
      setRelatorio(relatorioRes.data);
      setHistorico(
        (historicoRes.data.results || []).filter((item) => {
          const data = [item.dados_novos, item.dados_anteriores];
          return data.some(
            (registro) =>
              registro &&
              (registro.implantacao === implantacaoId ||
                registro.id === implantacaoId),
          );
        }),
      );

      return normalized;
    } catch (error) {
      if (detailRequestRef.current !== requestId) {
        return null;
      }
      console.error(error);
      messageApi.error("Nao foi possivel carregar o detalhe da implantacao.");
      return null;
    } finally {
      if (detailRequestRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  };

  // Carrega checklist da fase pela rota dedicada de controle-fase/checklist.
  const loadControleFaseChecklist = async (controleFaseId) => {
    if (!controleFaseId) return [];

    try {
      const response =
        await implantacaoRequest.getControleFaseChecklist(controleFaseId);
      const data = response?.data;
      const checklistRows = Array.isArray(data)
        ? data
        : data?.results ||
          data?.checklist ||
          data?.itens ||
          data?.respostas ||
          [];
      const selectedRows = (checklistRows || []).filter(isChecklistRowSelected);
      const rowsToUse =
        selectedRows.length || !checklistRows.length
          ? selectedRows
          : checklistRows;

      if (data && !Array.isArray(data)) {
        setSelectedImplantacao((prev) =>
          prev
            ? {
                ...prev,
                pode_iniciar_implantacao_real:
                  data?.pode_iniciar_implantacao_real ??
                  prev?.pode_iniciar_implantacao_real ??
                  null,
                motivo_bloqueio_implantacao_real:
                  data?.motivo_bloqueio_implantacao_real ??
                  prev?.motivo_bloqueio_implantacao_real ??
                  null,
              }
            : prev,
        );
      }

      return rowsToUse;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    loadSupportData();
  }, []);

  useEffect(() => {
    loadImplantacoes(1, 10, filters);
  }, [filters]);

  useEffect(() => {
    if (!detailVisible || !selectedImplantacaoId) return;
    loadImplantacaoDetail(selectedImplantacaoId);
  }, [detailVisible, selectedImplantacaoId]);

  // Calcula métricas do dashboard por status das implantações.
  const getDashboardMetrics = () => {
    const records = implantacoes.results || [];
    return {
      planejadas: records.filter((item) => item.status === "Planejada").length,
      andamento: records.filter((item) => item.status === "Em andamento")
        .length,
      concluidas: records.filter((item) => item.status === "Concluida").length,
      canceladas: records.filter((item) => item.status === "Cancelada").length,
    };
  };

  const metrics = getDashboardMetrics();

  // Abre modal de implantação para criação ou edição e preenche os campos.
  const openImplantacaoModal = async (record = null) => {
    setEditingEntity(record);
    setImplantacaoModalVisible(true);

    if (!record) {
      formImplantacao.resetFields();
      formImplantacao.setFieldsValue({
        status: "Planejada",
        status_financeiro: "Pendente",
        tipo_grupo: "A",
        responsavel_interno: currentUser?.id,
      });
      return;
    }

    let sourceRecord = record;
    if (!getSistemaIdsFromRecord(record).length && record?.id) {
      try {
        const { data } = await implantacaoRequest.getImplantacao(record.id);
        sourceRecord = { ...record, ...data };
      } catch {
        console.error("erro");
      }
    }

    const sistemasDoRecord = [
      ...(sourceRecord.sistemas_vinculados || []).map(
        (i) => i?.sistema || i?.id || i?.sistema_id,
      ),
      ...(sourceRecord.sistemas || []).map(
        (i) => i?.sistema || i?.id || i?.sistema_id,
      ),
    ];

    if (sistemasDoRecord.length) {
      setSistemas((prev) => {
        const map = new Map(
          (prev || []).map((item) => [String(item.id), item]),
        );
        sistemasDoRecord.forEach((item) => {
          const id = item?.id || item?.sistema || item?.sistema_id;
          if (id == null) return;
          const key = String(id);

          if (!map.has(key)) {
            map.set(key, {
              id,
              nome_sistema:
                item?.sistema_descricao ||
                item?.nome ||
                item?.descricao ||
                `Sistema ${id}`,
            });
          }
        });
        return Array.from(map.values());
      });
    }

    formImplantacao.setFieldsValue({
      ...sourceRecord,
      sistemas_ids: getSistemaIdsFromRecord(sourceRecord),
      responsavel_interno:
        sourceRecord.responsavel_interno || sourceRecord.responsavel_interno_id,
      responsavel_cliente:
        sourceRecord.responsavel_cliente || sourceRecord.responsavel_cliente_id,
      tipo_grupo:
        sourceRecord.tipo_grupo ||
        sourceRecord.grupo_tipo ||
        sourceRecord.tipo ||
        undefined,
      status_financeiro:
        sourceRecord.status_financeiro ||
        sourceRecord.financeiro_status ||
        undefined,
      data_inicio: sourceRecord.data_inicio
        ? moment(sourceRecord.data_inicio)
        : null,
      data_previsao_termino: sourceRecord.data_previsao_termino
        ? moment(sourceRecord.data_previsao_termino)
        : null,
      data_termino_efetiva: sourceRecord.data_termino_efetiva
        ? moment(sourceRecord.data_termino_efetiva)
        : null,
    });
  };

  // Salva implantação (create/update) e atualiza listagem e detalhe quando necessário.
  const handleSaveImplantacao = async (values) => {
    const isNew = !editingEntity?.id;
    const editingId = editingEntity?.id;
    const sistemasIds = (
      values.sistemas_ids || (values.sistema ? [values.sistema] : [])
    ).map((item) => {
      const numberValue = Number(item);
      return Number.isNaN(numberValue) ? item : numberValue;
    });
    const payload = {
      ...values,
      sistemas_ids: sistemasIds,
      data_inicio: formatDateForRequest(values.data_inicio),
      data_previsao_termino: formatDateForRequest(values.data_previsao_termino),
      data_termino_efetiva: formatDateForRequest(values.data_termino_efetiva),
    };

    setSaving(true);
    try {
      const response = await implantacaoRequest.saveImplantacao(
        payload,
        editingId,
      );

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar a implantacao.");
        return;
      }

      const savedRecord = response.data;
      upsertImplantacaoInList(savedRecord, isNew);
      messageApi.success(
        isNew
          ? "Implantacao criada com sucesso."
          : "Implantacao atualizada com sucesso.",
      );

      setImplantacaoModalVisible(false);
      setEditingEntity(null);
      formImplantacao.resetFields();

      await Promise.all([
        loadImplantacoes(1, implantacaoPageSize, filters, {
          silent: true,
          retry: true,
        }),
        loadSupportData(),
      ]);

      if (editingId && isSameId(selectedImplantacao?.id, editingId)) {
        await loadImplantacaoDetail(editingId);
      }
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel salvar a implantacao."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Exclui implantação após confirmação e recarrega a listagem.
  const handleDeleteImplantacao = (record) => {
    Modal.confirm({
      title: "Excluir implantacao",
      content: `Deseja remover a implantacao ${record.nome_cartorio || record.cartorio_descricao || record.id}?`,
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setSaving(true);
        try {
          const response = await implantacaoRequest.deleteImplantacao(
            record.id,
          );

          if (response.status !== 200 && response.status !== 204) {
            messageApi.error("Nao foi possivel excluir a implantacao.");
            return;
          }

          if (selectedImplantacao?.id === record.id) {
            detailRequestRef.current += 1;
            setDetailVisible(false);
            setDetailLoading(false);
            setSelectedImplantacaoId(null);
            setSelectedImplantacao(null);
            setSelectedPhase(null);
            setPhaseDetails({});
            setHistorico([]);
            setRelatorio(null);
            setFasesDisponiveis([]);
            setFaseParaAdicionar(null);
          }

          await loadImplantacoes();
          messageApi.success("Implantacao excluida.");
        } catch (error) {
          console.error(error);
          messageApi.error(
            getApiErrorMessage(
              error,
              "Nao foi possivel excluir a implantacao.",
            ),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Abre modal de cadastros básicos (categoria, fase ou item de checklist).
  const openBasicModal = (type, record = null) => {
    setEntityType(type);
    setEditingEntity(record);
    setBasicModalVisible(true);

    if (!record) {
      formBasic.resetFields();
      if (type === "itens") {
        formBasic.setFieldsValue({
          ativo: true,
          obrigatorio: true,
          marco_financeiro: "N",
        });
      }
      return;
    }

    formBasic.setFieldsValue({
      ...record,
      marco_financeiro: record?.marco_financeiro || "N",
      item_dependencia:
        getIdFromValue(record?.item_dependencia) || record?.item_dependencia_id,
      item_dependencia_descricao: record?.item_dependencia_descricao,
    });
  };

  // Salva cadastro básico conforme a aba ativa.
  const handleSaveBasic = async (values) => {
    const { item_dependencia_descricao, ...valuesWithoutReadonly } = values;
    const itemPayload =
      entityType === "itens"
        ? {
            ...valuesWithoutReadonly,
            marco_financeiro: valuesWithoutReadonly.marco_financeiro || "N",
            item_dependencia: valuesWithoutReadonly.item_dependencia || null,
          }
        : values;

    const handlers = {
      categorias: () =>
        implantacaoRequest.saveCategoria(values, editingEntity?.id),
      fases: () => implantacaoRequest.saveFase(values, editingEntity?.id),
      itens: () =>
        implantacaoRequest.saveItemChecklist(itemPayload, editingEntity?.id),
    };

    const successMessages = {
      categorias: "Categoria salva com sucesso.",
      fases: "Fase salva com sucesso.",
      itens: "Item do checklist salvo com sucesso.",
    };

    setSaving(true);
    try {
      const response = await handlers[entityType]();

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar o cadastro.");
        return;
      }

      setBasicModalVisible(false);
      setEditingEntity(null);
      formBasic.resetFields();
      await loadSupportData();
      messageApi.success(successMessages[entityType]);
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel salvar o cadastro."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Exclui cadastro básico (categoria, fase ou item) e recarrega dados de apoio.
  const handleDeleteBasic = (type, record) => {
    const handlers = {
      categorias: () => implantacaoRequest.deleteCategoria(record.id),
      fases: () => implantacaoRequest.deleteFase(record.id),
      itens: () => implantacaoRequest.deleteItemChecklist(record.id),
    };

    const labels = {
      categorias: "categoria",
      fases: "fase",
      itens: "item",
    };

    Modal.confirm({
      title: `Excluir ${labels[type]}`,
      content: `Deseja excluir ${record.nome || record.descricao}?`,
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setSaving(true);
        try {
          const response = await handlers[type]();

          if (response.status !== 200 && response.status !== 204) {
            messageApi.error("Nao foi possivel excluir o registro.");
            return;
          }

          await loadSupportData();
          messageApi.success(
            `${labels[type][0].toUpperCase() + labels[type].slice(1)} excluida com sucesso.`,
          );
        } catch (error) {
          console.error(error);
          messageApi.error(
            getApiErrorMessage(error, "Nao foi possivel excluir o registro."),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Une itens planejados do checklist com respostas já registradas da fase.

  // Abre o drawer de gerenciamento da fase e preenche o formulário.
  const getMergedChecklist = (phase) => {
    const bundle = phaseDetails[phase.id] || { responses: [] };
    const phaseItems = getChecklistItemsByPhase(phase);
    const phaseItemsById = new Map(
      phaseItems.map((item) => [String(item.id), item]),
    );

    const mergedByItem = new Map();

    (bundle.responses || []).forEach((response) => {
      const responseItemId = getChecklistResponseItemId(response);
      const item = phaseItemsById.get(String(responseItemId));
      const mapKey =
        responseItemId !== null && responseItemId !== undefined
          ? String(responseItemId)
          : `response-${response?.id}`;
      const current = mergedByItem.get(mapKey) || {};

      mergedByItem.set(mapKey, {
        ...(item || {}),
        ...current,
        id: responseItemId || response.id,
        itemId: responseItemId || null,
        descricao:
          response.descricao ||
          response?.item?.descricao ||
          current?.descricao ||
          item?.descricao ||
          `Item ${responseItemId || response?.id}`,
        categoria_descricao:
          current?.categoria_descricao ||
          (item
            ? getCategoriaDescricao(item)
            : getCategoriaDescricao(response)),
        obrigatorio:
          current?.obrigatorio ??
          (item ? getObrigatorioValue(item) : getObrigatorioValue(response)),
        responseId: getChecklistResponseId(response) || response?.id,
        status: response?.status || current?.status || "Pendente",
        observacao: response?.observacao ?? current?.observacao ?? null,
        responsavel: response?.responsavel ?? current?.responsavel ?? null,
        responsavel_nome:
          response?.responsavel_nome ?? current?.responsavel_nome ?? null,
        data_resposta:
          response?.data_resposta ?? current?.data_resposta ?? null,
        bloqueado: response?.bloqueado ?? current?.bloqueado ?? false,
        aguardando_financeiro:
          response?.aguardando_financeiro ??
          current?.aguardando_financeiro ??
          false,
        motivo_bloqueio:
          response?.motivo_bloqueio || current?.motivo_bloqueio || null,
        pode_responder:
          response?.pode_responder ?? current?.pode_responder ?? true,
        status_exibicao:
          response?.status_exibicao || current?.status_exibicao || null,
        marco_financeiro:
          response?.marco_financeiro ||
          response?.item?.marco_financeiro ||
          current?.marco_financeiro ||
          item?.marco_financeiro ||
          "N",
        item_dependencia:
          response?.item_dependencia ||
          response?.item?.item_dependencia ||
          current?.item_dependencia ||
          item?.item_dependencia ||
          null,
        item_dependencia_descricao:
          response?.item_dependencia_descricao ||
          response?.item?.item_dependencia_descricao ||
          current?.item_dependencia_descricao ||
          item?.item_dependencia_descricao ||
          null,
      });
    });

    return Array.from(mergedByItem.values()).sort(
      (a, b) => (a.ordem || 0) - (b.ordem || 0),
    );
  };

  const openPhaseDrawer = async (phase) => {
    setSelectedPhase(phase);
    setPhaseItemsToAdd([]);
    setPhaseVisible(true);
    formPhase.setFieldsValue({
      ...phase,
      fase_nome: getFaseDescricao(phase),
      data_inicio: phase.data_inicio ? moment(phase.data_inicio) : null,
      data_previsao: phase.data_previsao ? moment(phase.data_previsao) : null,
      data_conclusao: phase.data_conclusao
        ? moment(phase.data_conclusao)
        : null,
    });

    await loadControleFaseChecklist(phase.id);
  };

  // Salva alterações da fase no fluxo da implantação.
  const handleSavePhase = async (values) => {
    if (!selectedPhase) return;

    const payload = {
      ...selectedPhase,
      ...values,
      data_inicio: formatDateForRequest(values.data_inicio),
      data_previsao: formatDateForRequest(values.data_previsao),
      data_conclusao: formatDateForRequest(values.data_conclusao),
    };

    setSaving(true);
    try {
      const response = await implantacaoRequest.saveControleFase(
        payload,
        selectedPhase.id,
      );

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar a fase.");
        return;
      }

      setPhaseVisible(false);
      await loadImplantacaoDetail(selectedImplantacao.id);
      messageApi.success("Fase atualizada com sucesso.");
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel salvar a fase."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Adiciona uma fase disponível ao fluxo da implantação selecionada.
  const handleAddPhaseToImplantacao = async () => {
    if (!selectedImplantacao?.id || !faseParaAdicionar) {
      messageApi.info("Selecione uma fase para adicionar.");
      return;
    }

    const faseJaIncluida = (selectedImplantacao?.fases || []).some((phase) => {
      const faseIdAtual =
        getIdFromValue(phase?.fase) || phase?.fase_id || phase?.faseId;
      return isSameId(faseIdAtual, faseParaAdicionar);
    });
    if (faseJaIncluida) {
      messageApi.info("Essa fase ja foi adicionada nesta implantacao.");
      setFaseParaAdicionar(null);
      return;
    }

    const addPhaseWithChecklist = async (faseId) => {
      const response = await implantacaoRequest.saveControleFase({
        implantacao: selectedImplantacao.id,
        fase: faseId,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Nao foi possivel adicionar a fase.");
      }

      const createdPhase = response?.data || {};
      const createdPhaseId =
        createdPhase?.id ||
        createdPhase?.controle_fase_id ||
        createdPhase?.implantacao_fase_id;
      const faseCadastroId =
        getIdFromValue(createdPhase?.fase) ||
        createdPhase?.fase_id ||
        createdPhase?.faseId ||
        faseId;
      const defaultChecklistItems = getChecklistItemsByPhase({
        fase: faseCadastroId,
      });
      const defaultItemIds = defaultChecklistItems
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id));

      if (createdPhaseId && defaultItemIds.length && currentUser?.id) {
        let pendingItemIds = [...new Set(defaultItemIds)];
        const ignoredIds = new Set();

        while (pendingItemIds.length) {
          try {
            await implantacaoRequest.selecionarChecklistControleFase({
              implantacao_fase: createdPhaseId,
              itens_ids: pendingItemIds,
              responsavel: currentUser.id,
            });
            break;
          } catch (checklistError) {
            const notApplicableIds =
              getNotApplicableChecklistIdsFromError(checklistError);
            if (!notApplicableIds.length) {
              throw checklistError;
            }

            const nextPending = pendingItemIds.filter((itemId) => {
              const shouldIgnore = notApplicableIds.includes(itemId);
              if (shouldIgnore) ignoredIds.add(itemId);
              return !shouldIgnore;
            });

            if (nextPending.length === pendingItemIds.length) {
              break;
            }
            pendingItemIds = nextPending;
          }
        }

        if (ignoredIds.size) {
          messageApi.warning(
            `Alguns itens do checklist nao se aplicam ao grupo da implantacao e foram ignorados: [${Array.from(ignoredIds).join(", ")}].`,
          );
        }
      }

      const checklistRows = createdPhaseId
        ? await loadControleFaseChecklist(createdPhaseId)
        : [];

      return {
        ...createdPhase,
        id: createdPhaseId || createdPhase?.id,
        fase: createdPhase?.fase || faseCadastroId,
        fase_id: createdPhase?.fase_id || faseCadastroId,
        respostas_checklist: checklistRows,
      };
    };

    setSaving(true);
    try {
      const createdPhase = await addPhaseWithChecklist(faseParaAdicionar);

      setPhaseDetails((prev) => ({
        ...prev,
        [createdPhase.id]: {
          responses:
            createdPhase?.respostas_checklist ||
            createdPhase?.respostas ||
            createdPhase?.checklist_respostas ||
            [],
          validations: createdPhase?.validacoes || [],
        },
      }));

      messageApi.success("Fase adicionada com sucesso.");
      setFaseParaAdicionar(null);
      await loadImplantacaoDetail(selectedImplantacao.id);
    } catch (error) {
      console.error(error);
      const apiMessage = getApiErrorMessage(
        error,
        "Nao foi possivel adicionar a fase.",
      );
      const normalizedApiMessage = normalizeText(apiMessage);

      if (
        normalizedApiMessage.includes("itens nao aplic") &&
        normalizedApiMessage.includes("grupo da implant")
      ) {
        messageApi.warning(apiMessage);
        setFaseParaAdicionar(null);
        await loadImplantacaoDetail(selectedImplantacao.id);
        return;
      }

      if (normalizedApiMessage.includes("set unico")) {
        messageApi.info("Essa fase ja existe na implantacao.");
        await loadImplantacaoDetail(selectedImplantacao.id);
        return;
      }

      if (
        normalizedApiMessage.includes("pre implant") &&
        normalizedApiMessage.includes("set com implant")
      ) {
        const linkedImplantacaoPhase =
          findLinkedImplantacaoPhase(faseParaAdicionar);
        const linkedPhaseId = getFaseIdFromOption(linkedImplantacaoPhase);

        if (linkedPhaseId) {
          try {
            await addPhaseWithChecklist(linkedPhaseId);
          } catch (linkedError) {
            const linkedMessage = normalizeText(
              getApiErrorMessage(
                linkedError,
                "Nao foi possivel adicionar a fase vinculada.",
              ),
            );
            if (!linkedMessage.includes("set unico")) {
              throw linkedError;
            }
          }
          await addPhaseWithChecklist(faseParaAdicionar);
          messageApi.success("Fases adicionadas com sucesso.");
          setFaseParaAdicionar(null);
          await loadImplantacaoDetail(selectedImplantacao.id);
          return;
        }
      }

      messageApi.error(apiMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleIniciarImplantacaoReal = async () => {
    if (!selectedImplantacao?.id) return;

    if (selectedImplantacao?.pode_iniciar_implantacao_real === false) {
      messageApi.info(
        selectedImplantacao?.motivo_bloqueio_implantacao_real ||
          "A implantação real ainda está bloqueada.",
      );
      return;
    }

    setSaving(true);
    try {
      const response = await implantacaoRequest.iniciarImplantacaoReal(
        selectedImplantacao.id,
      );

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel iniciar a implantacao real.");
        return;
      }

      messageApi.success("Implantacao real iniciada com sucesso.");
      await loadImplantacaoDetail(selectedImplantacao.id);
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(
          error,
          "Nao foi possivel iniciar a implantacao real.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };
  // Insere itens selecionados no checklist da fase criando respostas em lote pendentes.
  const handleAddChecklistItemsToPhase = async () => {
    if (!selectedPhase?.id) return;
    if (!currentUser?.id) {
      messageApi.error("Nao foi possivel identificar o responsavel.");
      return;
    }

    if (!phaseItemsToAdd.length) {
      messageApi.info("Selecione ao menos um item para inserir na fase.");
      return;
    }

    const currentIds = new Set(
      (selectedPhaseBundle?.responses || [])
        .map((response) => String(getChecklistResponseItemId(response)))
        .filter(Boolean),
    );
    const uniqueToCreate = [
      ...new Set(phaseItemsToAdd.map((id) => String(id))),
    ].filter((id) => !currentIds.has(id));

    if (!uniqueToCreate.length) {
      messageApi.info("Os itens selecionados ja foram inseridos na fase.");
      return;
    }

    setSaving(true);
    try {
      const response = await implantacaoRequest.selecionarChecklistControleFase(
        {
          implantacao_fase: selectedPhase.id,
          itens_ids: uniqueToCreate.map((itemId) => Number(itemId)),
          responsavel: currentUser?.id,
        },
      );

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel inserir itens na fase.");
        return;
      }

      setPhaseItemsToAdd([]);
      await loadImplantacaoDetail(selectedImplantacao.id);
      await loadControleFaseChecklist(selectedPhase.id);
      messageApi.success("Itens inseridos na fase com sucesso.");
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel inserir itens na fase."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Abre modal para responder item do checklist e carrega evidências já anexadas.
  const openResponseModal = async (phase, checklistRow, checklistRows = []) => {
    if (!getChecklistItemCanRespond(checklistRow, checklistRows)) {
      messageApi.info(
        getChecklistItemBlockedReason(checklistRow, checklistRows) ||
          "Este item está bloqueado no momento.",
      );
      return;
    }

    setSelectedPhase(phase);
    setSelectedResponse(checklistRow);
    setResponseVisible(true);
    setResponseEvidences([]);

    formResponse.setFieldsValue({
      item_descricao: checklistRow.descricao,
      status: checklistRow.status || "Pendente",
      observacao: checklistRow.observacao,
      responsavel: checklistRow.responsavel || currentUser.id,
      data_resposta: checklistRow.data_resposta
        ? moment(checklistRow.data_resposta)
        : null,
    });

    if (checklistRow.responseId) {
      try {
        const response = await implantacaoRequest.getEvidencias(1, 100, {
          resposta_id: checklistRow.responseId,
          ordering: "-data_upload",
        });
        setResponseEvidences(response.data.results || []);
      } catch (error) {
        console.error(error);
      }
    }
  };

  // Salva resposta do checklist para o item selecionado.
  const handleSaveResponse = async (values) => {
    if (!getChecklistItemCanRespond(selectedResponse, currentChecklistRows)) {
      messageApi.info(
        getChecklistItemBlockedReason(selectedResponse, currentChecklistRows) ||
          "Este item está bloqueado e não pode ser respondido.",
      );
      return;
    }

    const payload = {
      implantacao_fase: selectedPhase.id,
      item: selectedResponse.id,
      status: values.status,
      observacao: values.observacao,
      responsavel: values.responsavel,
      data_resposta: formatDateForRequest(values.data_resposta),
    };

    setSaving(true);
    try {
      const response = await implantacaoRequest.saveRespostaChecklist(
        payload,
        selectedResponse.responseId,
      );

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar a resposta do checklist.");
        return;
      }

      setResponseVisible(false);
      await loadImplantacaoDetail(selectedImplantacao.id);
      messageApi.success("Resposta do checklist salva com sucesso.");
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(
          error,
          "Nao foi possivel salvar a resposta do checklist.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  // Abre modal de evidência para a resposta selecionada.
  const openEvidenceModal = (response) => {
    if (!response.responseId) {
      messageApi.info(
        "Salve a resposta do checklist antes de anexar evidencias.",
      );
      return;
    }

    setSelectedResponse(response);
    setEvidenceVisible(true);
    setUploadFileList([]);
    formEvidence.resetFields();
    formEvidence.setFieldsValue({ tipo: "Documento" });
  };

  // Salva evidência (upload de arquivo) vinculada à resposta do checklist.
  const handleSaveEvidence = async (values) => {
    if (!selectedResponse?.responseId) return;

    if (!uploadFileList.length) {
      messageApi.error("Selecione um arquivo para a evidencia.");
      return;
    }

    const formData = new FormData();
    formData.append("resposta", selectedResponse.responseId);
    formData.append("tipo", values.tipo);
    formData.append("titulo", values.titulo);
    formData.append("descricao", values.descricao || "");
    formData.append("usuario_upload", currentUser.id);
    formData.append("arquivo", uploadFileList[0].originFileObj);

    setSaving(true);
    try {
      const response = await implantacaoRequest.saveEvidencia(formData);

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar a evidencia.");
        return;
      }

      setEvidenceVisible(false);
      setUploadFileList([]);
      formEvidence.resetFields();

      const evidenciasResponse = await implantacaoRequest.getEvidencias(
        1,
        100,
        {
          resposta_id: selectedResponse.responseId,
          ordering: "-data_upload",
        },
      );
      setResponseEvidences(evidenciasResponse.data.results || []);

      await loadImplantacaoDetail(selectedImplantacao.id);
      messageApi.success("Evidencia anexada com sucesso.");
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel salvar a evidencia."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Exclui evidência e atualiza a listagem de anexos da resposta.
  const handleDeleteEvidence = (record) => {
    Modal.confirm({
      title: "Excluir evidencia",
      content: `Deseja remover a evidencia "${record.titulo}"?`,
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setSaving(true);
        try {
          const response = await implantacaoRequest.deleteEvidencia(record.id);

          if (response.status !== 200 && response.status !== 204) {
            messageApi.error("Nao foi possivel excluir a evidencia.");
            return;
          }

          if (selectedResponse?.responseId) {
            const evidenciasResponse = await implantacaoRequest.getEvidencias(
              1,
              100,
              {
                resposta_id: selectedResponse.responseId,
                ordering: "-data_upload",
              },
            );
            setResponseEvidences(evidenciasResponse.data.results || []);
          }

          messageApi.success("Evidencia removida com sucesso.");
        } catch (error) {
          console.error(error);
          messageApi.error(
            getApiErrorMessage(error, "Nao foi possivel excluir a evidencia."),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Abre modal para registrar validação do gestor na fase.
  const openValidationModal = (phase) => {
    setSelectedPhase(phase);
    setValidationVisible(true);
    formValidation.resetFields();
    formValidation.setFieldsValue({
      status: "Aprovado",
      validador: currentUser.id,
    });
  };

  // Salva validação do gestor para a fase selecionada.
  const handleSaveValidation = async (values) => {
    if (!selectedPhase) return;

    const payload = {
      implantacao_fase: selectedPhase.id,
      validador: values.validador,
      status: values.status,
      comentarios: values.comentarios,
    };

    setSaving(true);
    try {
      const response = await implantacaoRequest.saveValidacao(payload);

      if (response.status !== 200 && response.status !== 201) {
        messageApi.error("Nao foi possivel salvar a validacao.");
        return;
      }

      setValidationVisible(false);
      formValidation.resetFields();
      await loadImplantacaoDetail(selectedImplantacao.id);
      messageApi.success("Validacao registrada com sucesso.");
    } catch (error) {
      console.error(error);
      messageApi.error(
        getApiErrorMessage(error, "Nao foi possivel salvar a validacao."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Exclui validação do gestor e recarrega o detalhe da implantação.
  const handleDeleteValidation = (record) => {
    Modal.confirm({
      title: "Excluir validacao",
      content: "Deseja remover este registro de validacao?",
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setSaving(true);
        try {
          const response = await implantacaoRequest.deleteValidacao(record.id);

          if (response.status !== 200 && response.status !== 204) {
            messageApi.error("Nao foi possivel excluir a validacao.");
            return;
          }

          await loadImplantacaoDetail(selectedImplantacao.id);
          messageApi.success("Validacao removida com sucesso.");
        } catch (error) {
          console.error(error);
          messageApi.error(
            getApiErrorMessage(error, "Nao foi possivel excluir a validacao."),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Exclui item/resposta do checklist da fase conforme o tipo de registro.
  const handleDeleteChecklistItem = (record) => {
    if (!currentUser?.id) {
      messageApi.error("Nao foi possivel identificar o responsavel.");
      return;
    }

    Modal.confirm({
      title: "Excluir item da fase",
      content: "Deseja remover este item do checklist desta fase?",
      okText: "Excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        setSaving(true);

        try {
          const tipoGrupo = String(
            selectedImplantacao?.tipo_grupo ||
              selectedImplantacao?.grupo_tipo ||
              "",
          ).toUpperCase();
          const isGrupoB = tipoGrupo === "B";
          const checklistItemId =
            getChecklistResponseItemId(record) ||
            record?.itemId ||
            record?.item_id ||
            record?.item_checklist_id ||
            record?.checklist_item_id ||
            record?.id;
          const responseId =
            getChecklistResponseId(record) || record?.responseId || null;
          const payload = {
            implantacao_fase: selectedPhase?.id,
            responsavel: currentUser?.id,
          };

          if (
            isGrupoB &&
            responseId !== null &&
            responseId !== undefined &&
            responseId !== ""
          ) {
            payload.resposta_ids = [responseId];
          }

          if (
            !isGrupoB &&
            checklistItemId !== null &&
            checklistItemId !== undefined &&
            checklistItemId !== ""
          ) {
            payload.itens_ids = [checklistItemId];
          }

          if (isGrupoB && !payload.resposta_ids?.length) {
            messageApi.info(
              "Para o Grupo B, a resposta do checklist e obrigatoria para exclusao.",
            );
            return;
          }

          if (!payload.resposta_ids?.length && !payload.itens_ids?.length) {
            messageApi.error(
              "Nao foi possivel identificar o item para remocao.",
            );
            return;
          }

          let response;
          try {
            response =
              await implantacaoRequest.removerChecklistControleFase(payload);
          } catch (error) {
            const apiMessage = getApiErrorMessage(
              error,
              "Nao foi possivel excluir o item do checklist.",
            );
            const isRequiredFieldError = String(apiMessage)
              .toLowerCase()
              .includes("obrigat");

            if (
              !isGrupoB &&
              isRequiredFieldError &&
              responseId !== null &&
              responseId !== undefined &&
              responseId !== ""
            ) {
              response = await implantacaoRequest.removerChecklistControleFase({
                ...payload,
                resposta_ids: [responseId],
              });
            } else {
              throw error;
            }
          }

          if (response.status !== 200 && response.status !== 204) {
            messageApi.error("Nao foi possivel excluir o item do checklist.");
            return;
          }

          await loadImplantacaoDetail(selectedImplantacao.id);
          await loadControleFaseChecklist(selectedPhase.id);
          messageApi.success("Item do checklist removido com sucesso.");
        } catch (error) {
          console.error(error);
          messageApi.error(
            getApiErrorMessage(
              error,
              "Nao foi possivel excluir o item do checklist.",
            ),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };
  const implantacaoColumns = [
    { title: "ID", dataIndex: "id", width: 80 },
    {
      title: "Cartório",
      render: (_, record) => (
        <Space size={6}>
          <span>
            {record.nome_cartorio ||
              record.cartorio_descricao ||
              "Não informado"}
          </span>
          {getGrupoTag(record.tipo_grupo)}
        </Space>
      ),
    },
    {
      title: "Sistema",
      dataIndex: "sistema_descricao",
      render: (_, record) => <span>{getSistemaNomes(record)}</span>,
    },
    { title: "Cidade", dataIndex: "cidade" },
    { title: "UF", dataIndex: "uf", width: 80 },
    {
      title: "Responsável interno",
      dataIndex: "responsavel_interno_nome",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Financeiro",
      dataIndex: "status_financeiro",
      render: (status) => getStatusTag(status || "Pendente"),
    },
    {
      title: "Previsão",
      dataIndex: "data_previsao_termino",
      render: (value) => formatDate(value, false),
    },
    {
      title: "Ações",
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Visualizar">
            <EyeOutlined onClick={() => openDetailDrawer(record)} />
          </Tooltip>
          <Tooltip title="Editar">
            <EditOutlined onClick={() => openImplantacaoModal(record)} />
          </Tooltip>
          <Tooltip title="Excluir">
            <DeleteOutlined onClick={() => handleDeleteImplantacao(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const categoriaColumns = [
    { title: "Nome", dataIndex: "nome" },
    {
      title: "Descrição",
      dataIndex: "descricao",
      render: (value) => value || "-",
    },
    { title: "Ordem", dataIndex: "ordem", width: 100 },
    {
      title: "Atualização",
      dataIndex: "data_atualizacao",
      render: (value) => formatDate(value),
    },
    {
      title: "Ações",
      render: (_, record) => (
        <Space>
          <EditOutlined onClick={() => openBasicModal("categorias", record)} />
          <DeleteOutlined
            onClick={() => handleDeleteBasic("categorias", record)}
          />
        </Space>
      ),
    },
  ];

  const faseColumns = [
    { title: "Nome", dataIndex: "nome" },
    {
      title: "Descrição",
      dataIndex: "descricao",
      render: (value) => value || "-",
    },
    { title: "Ordem", dataIndex: "ordem", width: 100 },
    {
      title: "Cor",
      dataIndex: "cor",
      render: (value) =>
        value ? (
          <Tag color={value}>{value}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Ações",
      render: (_, record) => (
        <Space>
          <EditOutlined onClick={() => openBasicModal("fases", record)} />
          <DeleteOutlined onClick={() => handleDeleteBasic("fases", record)} />
        </Space>
      ),
    },
  ];

  const itemColumns = [
    {
      title: "Fase",
      dataIndex: "fase_descricao",
      render: (value) => value || "-",
    },
    {
      title: "Categoria",
      dataIndex: "categoria_descricao",
      render: (value) => value || "-",
    },
    { title: "Descrição", dataIndex: "descricao" },
    {
      title: "Obrigatório",
      dataIndex: "obrigatorio",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Sim" : "Não"}</Tag>
      ),
    },
    {
      title: "Marco financeiro",
      dataIndex: "marco_financeiro",
      render: (value) => (
        <Tag color={value === "S" ? "blue" : "default"}>
          {value === "S" ? "Sim" : "Não"}
        </Tag>
      ),
    },
    {
      title: "Dependência",
      dataIndex: "item_dependencia_descricao",
      render: (value, record) =>
        value || record?.item_dependencia_descricao || "-",
    },
    {
      title: "Ativo",
      dataIndex: "ativo",
      render: (value) => (
        <Tag color={value ? "green" : "red"}>{value ? "Sim" : "Não"}</Tag>
      ),
    },
    { title: "Ordem", dataIndex: "ordem", width: 100 },
    {
      title: "Ações",
      render: (_, record) => (
        <Space>
          <EditOutlined onClick={() => openBasicModal("itens", record)} />
          <DeleteOutlined onClick={() => handleDeleteBasic("itens", record)} />
        </Space>
      ),
    },
  ];

  const itemDependenciaOptions = itensChecklist
    .filter((item) => {
      if (entityType !== "itens") return true;
      if (
        selectedBasicFaseId !== undefined &&
        selectedBasicFaseId !== null &&
        selectedBasicFaseId !== ""
      ) {
        const faseId =
          getIdFromValue(item?.fase) || item?.fase_id || item?.faseId;
        if (!isSameId(faseId, selectedBasicFaseId)) return false;
      }
      if (editingEntity?.id && isSameId(item?.id, editingEntity?.id))
        return false;
      return true;
    })
    .map((item) => ({
      label: item?.descricao || `Item ${item?.id}`,
      value: item?.id,
    }));

  const currentChecklistRows = selectedPhase
    ? getMergedChecklist(selectedPhase)
    : [];
  const phaseChecklistCatalog = selectedPhase
    ? getChecklistItemsByPhase(selectedPhase)
    : [];
  const currentChecklistIds = new Set(
    (selectedPhaseBundle?.responses || [])
      .map((response) => String(getChecklistResponseItemId(response)))
      .filter(Boolean),
  );

  useEffect(() => {
    if (!phaseVisible) {
      return undefined;
    }

    const topScrollbar = checklistTopScrollRef.current;
    const tableScrollable = getChecklistScrollableElement();

    if (!topScrollbar || !tableScrollable) {
      const retry = window.setTimeout(syncChecklistTopScrollbar, 0);
      return () => window.clearTimeout(retry);
    }

    syncChecklistTopScrollbar();

    const handleTopScroll = () => {
      if (checklistScrollSyncRef.current) return;
      checklistScrollSyncRef.current = true;
      tableScrollable.scrollLeft = topScrollbar.scrollLeft;
      checklistScrollSyncRef.current = false;
    };

    const handleTableScroll = () => {
      if (checklistScrollSyncRef.current) return;
      checklistScrollSyncRef.current = true;
      topScrollbar.scrollLeft = tableScrollable.scrollLeft;
      checklistScrollSyncRef.current = false;
    };

    topScrollbar.addEventListener("scroll", handleTopScroll);
    tableScrollable.addEventListener("scroll", handleTableScroll);
    window.addEventListener("resize", syncChecklistTopScrollbar);

    return () => {
      topScrollbar.removeEventListener("scroll", handleTopScroll);
      tableScrollable.removeEventListener("scroll", handleTableScroll);
      window.removeEventListener("resize", syncChecklistTopScrollbar);
    };
  }, [phaseVisible, selectedPhase, currentChecklistRows]);

  const checklistOptionsToAdd = phaseChecklistCatalog
    .filter((item) => !currentChecklistIds.has(String(item.id)))
    .map((item) => ({
      label: `${item.descricao}${item.categoria_descricao ? ` - ${item.categoria_descricao}` : ""}`,
      value: String(item.id),
    }));

  const checklistColumns = [
    {
      title: "Item",
      render: (_, record) => {
        const blockReason = getChecklistItemBlockedReason(
          record,
          currentChecklistRows,
        );
        const isBlocked = !!blockReason;

        return (
          <div className={isBlocked ? "implantacao-checklist-item-locked" : ""}>
            <Space size={6}>
              {isBlocked ? <LockOutlined /> : null}
              <strong>{record.descricao}</strong>
            </Space>
            <div className="implantacao-subtle">
              {record.categoria_descricao || "Sem categoria"}
            </div>
            {blockReason ? (
              <Tag color={isBlocked ? "default" : "blue"}>{blockReason}</Tag>
            ) : null}
            {record?.aguardando_financeiro ? (
              <Tag color="gold">Aguardando financeiro</Tag>
            ) : null}
            {record?.motivo_bloqueio ? (
              <div className="implantacao-subtle">{record.motivo_bloqueio}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Obrigatório",
      dataIndex: "obrigatorio",
      width: 120,
      render: (value) => (
        <Tag color={value ? "green" : "default"}>{value ? "Sim" : "Não"}</Tag>
      ),
    },
    {
      title: "Marco financeiro",
      dataIndex: "marco_financeiro",
      width: 140,
      render: (value) => (
        <Tag color={value === "S" ? "blue" : "default"}>
          {value === "S" ? "Sim" : "Não"}
        </Tag>
      ),
    },
    {
      title: "Dependência",
      dataIndex: "item_dependencia_descricao",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (value, record) => {
        if (getChecklistItemBlockedReason(record, currentChecklistRows)) {
          return <Tag color="default">Bloqueado</Tag>;
        }
        return getStatusTag(value);
      },
    },
    {
      title: "Responsável",
      dataIndex: "responsavel_nome",
      render: (value) => value || "-",
    },
    {
      title: "Resposta",
      dataIndex: "data_resposta",
      width: 140,
      render: (value) => formatDate(value),
    },
    {
      title: "Ações",
      width: 170,
      render: (_, record) => {
        const canRespond = getChecklistItemCanRespond(
          record,
          currentChecklistRows,
        );
        const blockReason = getChecklistItemBlockedReason(
          record,
          currentChecklistRows,
        );

        return (
          <Space>
            <Tooltip title={canRespond ? "Responder checklist" : blockReason}>
              <CheckCircleOutlined
                className={!canRespond ? "implantacao-action-disabled" : ""}
                onClick={
                  canRespond
                    ? () =>
                        openResponseModal(
                          selectedPhase,
                          record,
                          currentChecklistRows,
                        )
                    : undefined
                }
              />
            </Tooltip>
            <Tooltip title={canRespond ? "Anexar evidência" : blockReason}>
              <CloudUploadOutlined
                className={!canRespond ? "implantacao-action-disabled" : ""}
                onClick={
                  canRespond ? () => openEvidenceModal(record) : undefined
                }
              />
            </Tooltip>

            <Tooltip title="Excluir item do checklist">
              <DeleteOutlined
                onClick={() => handleDeleteChecklistItem(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const validationColumns = [
    {
      title: "Validador",
      dataIndex: "validador_nome",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => getStatusTag(value),
    },
    {
      title: "Data",
      dataIndex: "data_validacao",
      render: (value) => formatDate(value),
    },
    {
      title: "Comentários",
      dataIndex: "comentarios",
      render: (value) => value || "-",
    },
    {
      title: "Ações",
      render: (_, record) => (
        <Tooltip title="Excluir validação">
          <DeleteOutlined onClick={() => handleDeleteValidation(record)} />
        </Tooltip>
      ),
    },
  ];

  const evidenceColumns = [
    { title: "Título", dataIndex: "titulo" },
    { title: "Tipo", dataIndex: "tipo", render: (value) => <Tag>{value}</Tag> },
    { title: "Upload por", dataIndex: "usuario_upload_nome" },
    {
      title: "Data",
      dataIndex: "data_upload",
      render: (value) => formatDate(value),
    },
    {
      title: "Arquivo",
      render: (_, record) =>
        record.arquivo_url ? (
          <a href={record.arquivo_url} target="_blank" rel="noreferrer">
            Abrir arquivo
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Ações",
      render: (_, record) => (
        <Tooltip title="Excluir evidência">
          <DeleteOutlined onClick={() => handleDeleteEvidence(record)} />
        </Tooltip>
      ),
    },
  ];

  const filterTags = Object.entries(filters)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(([key, value]) => (
      <Tag
        key={key}
        closable
        color="blue"
        onClose={() => {
          const next = { ...filters };
          delete next[key];
          setFilters(next);
        }}
      >
        {`${key}: ${value}`}
      </Tag>
    ));

  const reportImplantacao = relatorio?.implantacao || relatorio || {};
  const mergedReport = { ...(selectedImplantacao || {}), ...reportImplantacao };
  const reportFases = relatorio?.fases || reportImplantacao?.fases || [];
  const reportSistemaDescricao = getSistemaNomes(mergedReport);

  return (
    <div className="implantacao-page">
      {contextHolder}

      <div className="implantacao-hero">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={15}>
            <Title level={2}>Controle de Implantações</Title>
            <Paragraph style={{ color: "white", fontWeight: "500" }}>
              Gestão completa da jornada de pré-implantação, implantação e
              pós-implantação com checklist, evidências, validações e relatórios
              final por fase.
            </Paragraph>
          </Col>
          <Col xs={24} md={9}>
            <div className="implantacao-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openImplantacaoModal()}
                style={{
                  background: colors.secondary,
                  borderColor: colors.secondary,
                }}
              >
                Nova implantação
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <Row gutter={[16, 16]} className="implantacao-dashboard">
        <Col xs={24} md={6}>
          <Card className="implantacao-card">
            <Statistic
              title="Planejadas"
              value={metrics.planejadas}
              prefix={<FolderOpenOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="implantacao-card">
            <Statistic
              title="Em andamento"
              value={metrics.andamento}
              prefix={<ReloadOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="implantacao-card">
            <Statistic
              title="Concluídas"
              value={metrics.concluidas}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card className="implantacao-card">
            <Statistic
              title="Canceladas"
              value={metrics.canceladas}
              prefix={<DeleteOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card className="implantacao-card" style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Jornada recomendada
        </Title>
        <Space wrap>
          <Tag color="blue">1. Criar implantação com sistemas</Tag>
          <Tag color="cyan">2. Abrir fluxo da implantação</Tag>
          <Tag color="geekblue">3. Adicionar fases disponíveis</Tag>
          <Tag color="gold">4. Atualizar checklist por item</Tag>
          <Tag color="purple">5. Anexar evidências</Tag>
          <Tag color="green">6. Registrar validações e exportar PDF</Tag>
        </Space>
      </Card>

      <Tabs defaultActiveKey="operacao">
        <TabPane tab="Operação" key="operacao">
          <Card className="implantacao-card">
            <div className="implantacao-toolbar">
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>
                  Dashboard principal
                </Title>
                <Text type="secondary">
                  Visão geral das implantações planejadas, em andamento e
                  concluídas.
                </Text>
              </div>

              <div className="implantacao-toolbar-right">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setDrawerFilterVisible(true)}
                >
                  Filtros
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadImplantacoes()}
                >
                  Atualizar
                </Button>
              </div>
            </div>

            {!!filterTags.length && (
              <div style={{ marginBottom: 12 }}>{filterTags}</div>
            )}

            <Table
              rowKey="id"
              loading={loading}
              columns={implantacaoColumns}
              dataSource={implantacoes.results}
              pagination={{
                total: implantacoes.count,
                current: implantacaoPage,
                pageSize: implantacaoPageSize,
                showTotal: (total) => `Quantidade: ${total}`,
                onChange: (page, pageSize) => loadImplantacoes(page, pageSize),
              }}
              scroll={{ x: true }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Cadastros Básicos" key="cadastros">
          <Card className="implantacao-card">
            <Tabs activeKey={activeCadastroTab} onChange={setActiveCadastroTab}>
              <TabPane tab="Categorias" key="categorias">
                <div className="implantacao-toolbar">
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      Categorias
                    </Title>
                    <Text type="secondary">
                      Infraestrutura, dados, validação, treinamento e mais.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{
                      background: colors.secondary,
                      borderColor: colors.secondary,
                    }}
                    onClick={() => openBasicModal("categorias")}
                  >
                    Nova categoria
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  columns={categoriaColumns}
                  dataSource={categorias}
                  pagination={false}
                />
              </TabPane>

              <TabPane tab="Fases" key="fases">
                <div className="implantacao-toolbar">
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      Fases
                    </Title>
                    <Text type="secondary">
                      Pré-implantação, implantação e pós-implantação.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{
                      background: colors.secondary,
                      borderColor: colors.secondary,
                    }}
                    onClick={() => openBasicModal("fases")}
                  >
                    Nova fase
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  columns={faseColumns}
                  dataSource={fases}
                  pagination={false}
                />
              </TabPane>

              <TabPane tab="Itens do Checklist" key="itens">
                <div className="implantacao-toolbar">
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      Itens do checklist
                    </Title>
                    <Text type="secondary">
                      Checklist configuração por fase e categoria.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{
                      background: colors.secondary,
                      borderColor: colors.secondary,
                    }}
                    onClick={() => openBasicModal("itens")}
                  >
                    Novo item
                  </Button>
                </div>
                <Table
                  rowKey="id"
                  columns={itemColumns}
                  dataSource={itensChecklist}
                  scroll={{ x: true }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </TabPane>
      </Tabs>

      <Drawer
        title="Filtros de implantação"
        visible={drawerFilterVisible}
        onClose={() => setDrawerFilterVisible(false)}
        width={420}
      >
        <Form
          form={formFilter}
          layout="vertical"
          onFinish={(values) => {
            const nextFilters = {
              ...values,
              nome_cartorio: values.nome_cartorio || undefined,
              cidade: values.cidade || undefined,
              uf: values.uf || undefined,
              sistema_id: values.sistema_id || undefined,
              responsavel_interno_id:
                values.responsavel_interno_id || undefined,
              status: values.status || undefined,
              tipo_grupo: values.tipo_grupo || undefined,
              status_financeiro: values.status_financeiro || undefined,
            };
            setFilters(nextFilters);
            setDrawerFilterVisible(false);
          }}
        >
          <Form.Item name="nome_cartorio" label="Nome do cartório">
            <Input />
          </Form.Item>
          <Form.Item name="cidade" label="Cidade">
            <Input />
          </Form.Item>
          <Form.Item name="uf" label="UF">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={ufOptions}
            />
          </Form.Item>
          <Form.Item name="sistema_id" label="Sistema">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={sistemas.map((item) => ({
                label: item.nome_sistema,
                value: item.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="responsavel_interno_id" label="Responsável interno">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={usuarios.map((item) => ({
                label: item.nome || item.email,
                value: item.id,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={statusImplantacaoOptions}
            />
          </Form.Item>
          <Form.Item name="tipo_grupo" label="Tipo grupo">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={tipoGrupoOptions}
            />
          </Form.Item>
          <Form.Item name="status_financeiro" label="Status financeiro">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={statusFinanceiroOptions}
            />
          </Form.Item>

          <Space>
            <Button
              htmlType="submit"
              type="primary"
              style={{
                background: colors.secondary,
                borderColor: colors.secondary,
              }}
            >
              Aplicar filtros
            </Button>
            <Button
              onClick={() => {
                formFilter.resetFields();
                setFilters({});
                setDrawerFilterVisible(false);
              }}
            >
              Limpar
            </Button>
          </Space>
        </Form>
      </Drawer>

      <ImplantacaoFormModal
        visible={implantacaoModalVisible}
        onCancel={() => {
          setImplantacaoModalVisible(false);
          setEditingEntity(null);
          formImplantacao.resetFields();
        }}
        onSubmit={handleSaveImplantacao}
        loading={saving}
        form={formImplantacao}
        sistemas={sistemas}
        cartorios={cartorios}
        usuarios={usuarios}
      />

      <EntityModal
        title={
          entityType === "categorias"
            ? "Cadastro de categoria"
            : entityType === "fases"
              ? "Cadastro de fase"
              : "Cadastro de item do checklist"
        }
        visible={basicModalVisible}
        onCancel={() => {
          setBasicModalVisible(false);
          setEditingEntity(null);
          formBasic.resetFields();
        }}
        onSubmit={handleSaveBasic}
        loading={saving}
        form={formBasic}
      >
        {entityType === "categorias" && (
          <>
            <Form.Item
              name="nome"
              label="Nome"
              rules={[
                { required: true, message: "Informe o nome da categoria" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="descricao" label="Descrição">
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item name="ordem" label="Ordem">
              <Input type="number" />
            </Form.Item>
          </>
        )}

        {entityType === "fases" && (
          <>
            <Form.Item
              name="nome"
              label="Nome"
              rules={[{ required: true, message: "Informe o nome da fase" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="descricao" label="Descrição">
              <TextArea rows={4} />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="ordem" label="Ordem">
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="cor" label="Cor">
                  <Input type="color" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {entityType === "itens" && (
          <>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="fase"
                  label="Fase"
                  rules={[{ required: true, message: "Selecione a fase" }]}
                >
                  <Select
                    options={fases.map((item) => ({
                      label: item.nome,
                      value: item.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="categoria" label="Categoria">
                  <Select
                    allowClear
                    options={categorias.map((item) => ({
                      label: item.nome,
                      value: item.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="descricao"
              label="Descrição"
              rules={[
                { required: true, message: "Informe a descrição do item" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="instrucoes_adicionais"
              label="Instruções adicionais"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="ordem" label="Ordem">
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="marco_financeiro" label="Marco financeiro">
                  <Select options={marcoFinanceiroOptions} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="item_dependencia" label="Item dependência">
                  <Select
                    allowClear
                    showSearch
                    options={itemDependenciaOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="item_dependencia_descricao"
              label="Dependência (somente leitura)"
            >
              <Input disabled />
            </Form.Item>

            <Col span={8}>
              <Form.Item name="aplicavel" label="Aplicável">
                <Select
                  options={[
                    { value: "GRUPO_A", label: "Grupo A" },
                    { value: "GRUPO_B", label: "Grupo B" },
                    { value: "AMBOS", label: "Ambos" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item
                  name="obrigatorio"
                  label="Obrigatório"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="ativo" label="Ativo" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={8} />
            </Row>
          </>
        )}
      </EntityModal>

      <Drawer
        title={
          selectedImplantacao
            ? `Implantação #${selectedImplantacao.id} - ${
                selectedImplantacao.nome_cartorio ||
                selectedImplantacao.cartorio_descricao ||
                "Sem cartório"
              }`
            : "Detalhe da implantação"
        }
        visible={detailVisible}
        onClose={() => {
          detailRequestRef.current += 1;
          setDetailVisible(false);
          setDetailLoading(false);
          setSelectedImplantacaoId(null);
          setSelectedImplantacao(null);
          setSelectedPhase(null);
          setPhaseDetails({});
          setHistorico([]);
          setRelatorio(null);
          setFasesDisponiveis([]);
          setFaseParaAdicionar(null);
        }}
        width={1100}
      >
        {detailLoading || !selectedImplantacao ? (
          <Card loading />
        ) : (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Sistema">
                {(selectedImplantacao.sistemas_vinculados || [])
                  .map((item) => item.sistema_descricao || "")
                  .filter(Boolean)
                  .join(", ") ||
                  selectedImplantacao.sistema_descricao ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(selectedImplantacao.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Cartório">
                <Space size={6}>
                  <span>
                    {selectedImplantacao.nome_cartorio ||
                      selectedImplantacao.cartorio_descricao ||
                      "-"}
                  </span>
                  {getGrupoTag(selectedImplantacao.tipo_grupo)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status financeiro">
                {getStatusTag(
                  selectedImplantacao.status_financeiro || "Pendente",
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo grupo">
                {selectedImplantacao.tipo_grupo || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Responsável interno">
                {selectedImplantacao.responsavel_interno_nome || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Responsável cliente">
                {selectedImplantacao.responsavel_cliente_nome || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Cidade/UF">
                {[selectedImplantacao.cidade, selectedImplantacao.uf]
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Início">
                {formatDate(selectedImplantacao.data_inicio)}
              </Descriptions.Item>
              <Descriptions.Item label="Previsão de término">
                {formatDate(selectedImplantacao.data_previsao_termino)}
              </Descriptions.Item>
              <Descriptions.Item label="Término efetivo">
                {formatDate(selectedImplantacao.data_termino_efetiva)}
              </Descriptions.Item>
              <Descriptions.Item label="Versão banco">
                {selectedImplantacao.versao_banco || "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div className="implantacao-toolbar">
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>
                  Fases da implantação
                </Title>
                <Text type="secondary">
                  Controle operacional por fase, com checklist, evidências e
                  validações.
                </Text>
              </div>
              <div className="implantacao-toolbar-right">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => setReportVisible(true)}
                >
                  Relatório consolidado
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => openImplantacaoModal(selectedImplantacao)}
                >
                  Editar implantação
                </Button>
                <Tooltip
                  title={
                    selectedImplantacao?.pode_iniciar_implantacao_real === false
                      ? selectedImplantacao?.motivo_bloqueio_implantacao_real ||
                        "A implantação real ainda está bloqueada."
                      : ""
                  }
                >
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    style={{
                      background: colors.secondary,
                      borderColor: colors.secondary,
                    }}
                    onClick={handleIniciarImplantacaoReal}
                    disabled={
                      selectedImplantacao?.pode_iniciar_implantacao_real ===
                      false
                    }
                    loading={saving}
                  >
                    Iniciar Implantação Real
                  </Button>
                </Tooltip>
              </div>
            </div>

            <div className="implantacao-toolbar" style={{ marginBottom: 20 }}>
              <div>
                <Title level={5} style={{ marginBottom: 4 }}>
                  Adicionar fase
                </Title>
                <Text type="secondary">
                  Selecione uma fase disponível para incluir nesta implantação.
                </Text>
              </div>
              <Space wrap>
                <Select
                  value={faseParaAdicionar}
                  onChange={setFaseParaAdicionar}
                  placeholder="Escolha uma fase"
                  style={{ minWidth: 260 }}
                  options={fasesDisponiveis.map((item) => ({
                    value: item.fase_id || item.id || item.value,
                    label:
                      item.nome ||
                      item.descricao ||
                      item.fase_descricao ||
                      item.label ||
                      `Fase ${item.id}`,
                  }))}
                  disabled={!fasesDisponiveis.length}
                />
                <Button
                  type="primary"
                  style={{
                    background: colors.secondary,
                    borderColor: colors.secondary,
                  }}
                  onClick={handleAddPhaseToImplantacao}
                  disabled={!fasesDisponiveis.length || !faseParaAdicionar}
                  loading={saving}
                >
                  Adicionar fase
                </Button>
              </Space>
            </div>

            <Row gutter={[16, 16]}>
              {selectedImplantacao.fases?.map((phase) => {
                const mergedChecklist = getMergedChecklist(phase);
                const validations = phaseDetails[phase.id]?.validations || [];
                const progress = getChecklistProgress(mergedChecklist);
                const approved = validations.filter(
                  (item) => item.status === "Aprovado",
                ).length;

                return (
                  <Col xs={24} key={phase.id}>
                    <div className="implantacao-phase-card">
                      <div className="implantacao-phase-header">
                        <div>
                          <Title level={5} style={{ marginBottom: 4 }}>
                            {getFaseDescricao(phase)}
                          </Title>
                          <Space wrap>
                            {getStatusTag(phase.status)}
                            <Text type="secondary">
                              Início: {formatDate(phase.data_inicio)}
                            </Text>
                            <Text type="secondary">
                              Previsão: {formatDate(phase.data_previsao)}
                            </Text>
                          </Space>
                        </div>

                        <Space wrap>
                          <Button
                            icon={<SafetyCertificateOutlined />}
                            onClick={() => openValidationModal(phase)}
                          >
                            Validar fase
                          </Button>
                          <Button
                            type="primary"
                            style={{
                              background: colors.secondary,
                              borderColor: colors.secondary,
                            }}
                            onClick={() => openPhaseDrawer(phase)}
                          >
                            Gerenciar fase
                          </Button>
                        </Space>
                      </div>

                      <div className="implantacao-phase-metrics">
                        <div>
                          <strong>Checklist</strong>
                          <span>{mergedChecklist.length} itens planejados</span>
                        </div>
                        <div>
                          <strong>Progresso</strong>
                          <span>{progress}% concluído</span>
                        </div>
                        <div>
                          <strong>Validações</strong>
                          <span>{validations.length} registros</span>
                        </div>
                        <div>
                          <strong>Aprovações</strong>
                          <span>{approved} aprovadas</span>
                        </div>
                      </div>

                      <Progress
                        percent={progress}
                        strokeColor={colors.secondary}
                      />

                      <div className="implantacao-checklist-summary">
                        {mergedChecklist.slice(0, 4).map((item) => (
                          <Tag
                            key={`${phase.id}-${item.id}`}
                            color={statusColors[item.status] || "default"}
                          >
                            {item.descricao}
                          </Tag>
                        ))}
                        {mergedChecklist.length > 4 && (
                          <Tag>+{mergedChecklist.length - 4} itens</Tag>
                        )}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            <Divider />

            <Title level={4}>Histórico</Title>
            {historico.length ? (
              <List
                dataSource={historico}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${item.acao} em ${item.tabela_afetada}`}
                      description={`${item.usuario_nome} em ${formatDate(item.data_alteracao)}`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="Sem histórico relacionado carregado." />
            )}
          </>
        )}
      </Drawer>

      <Drawer
        title={
          selectedPhase
            ? `Gerenciar fase - ${getFaseDescricao(selectedPhase)}`
            : "Gerenciar fase"
        }
        className="implantacao-phase-drawer"
        visible={phaseVisible}
        onClose={() => {
          setPhaseVisible(false);
          setPhaseItemsToAdd([]);
        }}
        width={isSmallPhaseDrawer ? "100%" : "70%"}
      >
        {selectedPhase ? (
          <>
            <Form form={formPhase} layout="vertical" onFinish={handleSavePhase}>
              <Row gutter={12}>
                <Col xs={24}>
                  <Form.Item name="fase_nome" label="Fase">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="status" label="Status">
                    <Select options={statusFaseOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="data_inicio" label="Data de início">
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="data_previsao" label="Previsão">
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={24} md={8}>
                  <Form.Item name="data_conclusao" label="Conclusão">
                    <DatePicker
                      showTime
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                  <Form.Item name="observacoes" label="Observações">
                    <TextArea rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    background: colors.secondary,
                    borderColor: colors.secondary,
                  }}
                  loading={saving}
                >
                  Salvar fase
                </Button>
                <Button onClick={() => openValidationModal(selectedPhase)}>
                  Nova validação
                </Button>
              </Space>
            </Form>

            <Divider />

            <Title level={5}>Inserir itens na fase</Title>
            <Space
              direction="vertical"
              size={8}
              style={{ width: "100%", marginBottom: 16 }}
            >
              <Select
                mode="multiple"
                value={phaseItemsToAdd}
                onChange={setPhaseItemsToAdd}
                placeholder="Selecione os itens da fase"
                options={checklistOptionsToAdd}
                style={{ width: "100%" }}
                maxTagCount="responsive"
              />
              <Button
                type="primary"
                onClick={handleAddChecklistItemsToPhase}
                disabled={!phaseItemsToAdd.length}
                loading={saving}
                style={{
                  background: colors.secondary,
                  borderColor: colors.secondary,
                }}
              >
                Inserir itens selecionados
              </Button>
            </Space>

            <Divider />

            <Title level={4}>Checklist da fase</Title>
            <div className="implantacao-phase-table-wrap">
              <div
                className="implantacao-phase-table-top-scroll"
                ref={checklistTopScrollRef}
              >
                <div ref={checklistTopScrollInnerRef} />
              </div>
              <div ref={checklistTableWrapRef}>
                <Table
                  rowKey={(record) => `${selectedPhase.id}-${record.id}`}
                  columns={checklistColumns}
                  dataSource={currentChecklistRows}
                  rowClassName={(record) =>
                    getChecklistItemBlockedReason(record, currentChecklistRows)
                      ? "implantacao-row-blocked"
                      : ""
                  }
                  pagination={false}
                  scroll={{ x: 980 }}
                />
              </div>
            </div>

            <Divider />

            <Title level={4}>Validações do gestor</Title>
            <Table
              rowKey="id"
              columns={validationColumns}
              dataSource={selectedPhaseBundle?.validations || []}
              pagination={false}
              scroll={{ x: 640 }}
              locale={{ emptyText: "Nenhuma validação registrada." }}
            />
          </>
        ) : null}
      </Drawer>

      <EntityModal
        title="Responder checklist"
        visible={responseVisible}
        onCancel={() => {
          setResponseVisible(false);
          setSelectedResponse(null);
          formResponse.resetFields();
        }}
        onSubmit={handleSaveResponse}
        loading={saving}
        form={formResponse}
      >
        <Form.Item name="item_descricao" label="Item">
          <Input disabled />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Selecione o status" }]}
            >
              <Select options={statusChecklistOptions} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="responsavel"
              label="Responsável"
              rules={[{ required: true, message: "Selecione o Responsável" }]}
            >
              <Select
                options={usuarios.map((item) => ({
                  label: item.nome || item.email,
                  value: item.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="data_resposta" label="Data da resposta">
              <DatePicker
                showTime
                format={"DD/MM/YYYY HH:mm"}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="observacao" label="Observação">
          <TextArea rows={4} />
        </Form.Item>

        <Divider />
        <div className="implantacao-toolbar">
          <Title level={5} style={{ margin: 0 }}>
            Evidências anexadas
          </Title>
          <Button
            icon={<CloudUploadOutlined />}
            onClick={() => openEvidenceModal(selectedResponse)}
          >
            Nova evidência
          </Button>
        </div>
        <Table
          rowKey="id"
          columns={evidenceColumns}
          dataSource={responseEvidences}
          pagination={false}
          locale={{ emptyText: "Nenhuma evidência anexada." }}
          size="small"
        />
      </EntityModal>

      <EntityModal
        title="Anexar evidência"
        visible={evidenceVisible}
        onCancel={() => {
          setEvidenceVisible(false);
          formEvidence.resetFields();
          setUploadFileList([]);
        }}
        onSubmit={handleSaveEvidence}
        loading={saving}
        form={formEvidence}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="tipo"
              label="Tipo"
              rules={[
                { required: true, message: "Selecione o tipo da evidência" },
              ]}
            >
              <Select options={tipoEvidenciaOptions} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="titulo"
              label="Título"
              rules={[{ required: true, message: "Informe o título" }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="descricao" label="Descrição">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Arquivo">
          <Upload
            accept=".txt"
            beforeUpload={(file) => {
              setUploadFileList([file]);
              return false;
            }}
            onRemove={() => setUploadFileList([])}
            fileList={uploadFileList}
            maxCount={1}
          >
            <Button icon={<CloudUploadOutlined />}>Selecionar arquivo</Button>
          </Upload>
        </Form.Item>
      </EntityModal>

      <EntityModal
        title="Registrar validação do gestor"
        visible={validationVisible}
        onCancel={() => {
          setValidationVisible(false);
          formValidation.resetFields();
        }}
        onSubmit={handleSaveValidation}
        loading={saving}
        form={formValidation}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="validador"
              label="Validador"
              rules={[{ required: true, message: "Selecione o validador" }]}
            >
              <Select
                options={usuarios.map((item) => ({
                  label: item.nome || item.email,
                  value: item.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Selecione o status" }]}
            >
              <Select options={statusValidacaoOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="comentarios" label="Comentários">
          <TextArea rows={4} />
        </Form.Item>
      </EntityModal>

      <Drawer
        title="Relatório consolidado da implantação"
        visible={reportVisible}
        onClose={() => setReportVisible(false)}
        width={900}
        extra={
          relatorio ? (
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              style={{
                background: colors.secondary,
                borderColor: colors.secondary,
              }}
              onClick={exportReportToPdf}
            >
              Exportar PDF
            </Button>
          ) : null
        }
      >
        {!relatorio ? (
          <Empty description="Relatório indisponível." />
        ) : (
          <div className="implantacao-report" ref={reportContentRef}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Implantação">
                {mergedReport.id}
              </Descriptions.Item>
              <Descriptions.Item label="Sistema">
                {reportSistemaDescricao}
              </Descriptions.Item>
              <Descriptions.Item label="Cartório">
                <Space size={6}>
                  <span>
                    {mergedReport.nome_cartorio ||
                      mergedReport.cartorio_descricao ||
                      "-"}
                  </span>
                  {getGrupoTag(mergedReport.tipo_grupo)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {getStatusTag(mergedReport.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Status financeiro">
                {getStatusTag(mergedReport.status_financeiro || "Pendente")}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo grupo">
                {mergedReport.tipo_grupo || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Responsável interno">
                {mergedReport.responsavel_interno_nome || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Responsável cliente">
                {mergedReport.responsavel_cliente_nome || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Cidade / UF">
                {`${mergedReport.cidade || "-"} / ${mergedReport.uf || "-"}`}
              </Descriptions.Item>
              <Descriptions.Item label="CPF/CNPJ">
                {mergedReport.cpfcnpj || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="CNS">
                {mergedReport.cns || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Situação serventia">
                {mergedReport.situacao_serventia || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Versão banco">
                {mergedReport.versao_banco || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Início">
                {formatDate(mergedReport.data_inicio)}
              </Descriptions.Item>
              <Descriptions.Item label="Previsão término">
                {formatDate(mergedReport.data_previsao_termino)}
              </Descriptions.Item>
              <Descriptions.Item label="Término efetivo">
                {formatDate(mergedReport.data_termino_efetiva)}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {reportFases.map((fase) => (
              <Card
                key={fase.id}
                title={fase.fase}
                size="small"
                className="implantacao-card"
                style={{ marginBottom: 16 }}
              >
                <Paragraph>
                  Status: {getStatusTag(fase.status)} | Início:{" "}
                  {formatDate(fase.data_inicio)} | Previsão:{" "}
                  {formatDate(fase.data_previsao)} | Conclusão:{" "}
                  {formatDate(fase.data_conclusao)}
                </Paragraph>
                <Paragraph>{fase.observacoes || "Sem observações."}</Paragraph>

                <Title level={5}>Checklist</Title>
                <List
                  dataSource={fase.respostas || []}
                  locale={{ emptyText: "Sem itens no relatório." }}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.item} ${item.categoria ? `| ${item.categoria}` : ""}`}
                        description={`${item.status} | ${item.observacao || "Sem observação"} | Respondido em ${formatDate(item.data_resposta)}`}
                      />
                    </List.Item>
                  )}
                />

                <Divider />
                <Title level={5}>Validações</Title>
                <List
                  dataSource={fase.validacoes || []}
                  locale={{ emptyText: "Sem Validações." }}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.validador} - ${item.status}`}
                        description={`${formatDate(item.data_validacao)} | ${item.comentarios || "Sem comentários"}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            ))}

            <Divider />
            <div
              className="signature-block"
              style={{
                display: "flex",
                justifyItems: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Title level={5}>Assinatura</Title>
              <div className="signature-line" />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ImplantacaoPage;
